/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { brand } from '../../../../theme/themePrimitives';
import CardWrapper from '../../../wrapper/card';
import CustomTooltip from '../../../reusable/custom_tooltip';
import D7FilterPanel from './filter_panel';
import { deleteProspectItemThunk } from '../../../../redux/thunks/prospect';
import { getAllPipelinesThunk } from '../../../../redux/thunks/pipeline';
import RenderIcons from '../../../reusable/render_icons';
import ReviewProspect from '../../modal/review_selected_prospect';
import SortProspectsModal from '../../modal/sort_prospect';
import { AddOutlined, DeleteOutlineRounded, FileDownloadOutlined, FilterAltOutlined, SwapVertOutlined } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../../../redux/store';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { D7MetaKeys, MetaObject, ProspectDeal } from '../../../../params/prospect';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface ProspectsRow {
    id: number;
    name: string;
    location: string;
    // sublocation: string;
    contacts: MetaObject[];
    created_at: string;
}

const exportToCSV = (rows: ProspectsRow[], fileName: string) => {
    if (rows.length === 0) return;

    // Extract all unique meta_keys dynamically
    const allMetaKeys: Set<D7MetaKeys> = new Set();
    rows.forEach((row) => {
        row.contacts.forEach((contact) => allMetaKeys.add(contact.meta_key as D7MetaKeys));
    });

    // Convert Set to Array and Sort Alphabetically
    const metaKeysArray = Array.from(allMetaKeys).sort();

    // Define CSV Headers (Static + Dynamic Meta Keys)
    const headers = ['ID', 'Business', 'Location', 'Created At', ...metaKeysArray];
    // Create CSV Content
    const csvContent = [
        headers,
        ...rows.map((row) => {
            // Convert contacts array to a dictionary for easier lookup
            const contactMap: Partial<Record<D7MetaKeys, string>> = {};
            row.contacts.forEach((contact) => {
                contactMap[contact.meta_key as D7MetaKeys] = contact.meta_value;
            });

            // Map row data according to headers
            return [
                row.id,
                row.name,
                row.location,
                row.created_at,
                ...metaKeysArray.map((key) => contactMap[key] || '')
            ];
        })
    ]
        .map((row) => row.map((cell) => `"${cell}"`).join(','))
        .join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

interface ViewProspectsProps {
    setIsProspectOpen: () => void;
}

const ViewProspects: React.FC<ViewProspectsProps> = ({ setIsProspectOpen }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { prospectDetail } = useSelector((state: RootState) => state.prospect);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isSortOpen, setIsSortOpen] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
    const [sortField, setSortField] = useState<D7MetaKeys | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [downhillMode, setDownhillMode] = useState<boolean>(false);
    const [filteredResults, setFilteredResults] = useState<ProspectsRow[]>([]);
    // const [city, setCity] = useState('');
    const [city, setCity] = useState<string[]>([]);
    const [zip, setZip] = useState('');
    // const [paginationModel, setPaginationModel] = useState({
    //     pageSize: 50,
    //     page: 0
    // });
    const [searchCategory, setSearchCategory] = useState<number[]>([]);
    // const [searchCategory, setSearchCategory] = useState<string>('');
    const [selectedFilters, setSelectedFilters] = useState<Record<string, boolean>>({});

    const results = prospectDetail?.results || [];
    // const sortedResults = [...results].sort((a, b) => );

    const rows: ProspectsRow[] = results.map((business: ProspectDeal) => ({
        id: business.id || 0,
        name: business.title || '',
        // location: prospectDetail?.city as string || '',
        location: business.meta.find((c) => c.meta_key === 'address2')?.meta_value || '-',
        contacts: business.meta || [],
        created_at: business.created_at
    })) || [];
    /**
     * Sort rows dynamically based on the selected sort field.
     */
    useEffect(() => {
        let updatedResults = [...rows];

        // ✅ Downhill Mode Sorting (ID as priority)
        if (downhillMode) {
            updatedResults = updatedResults.filter((row) => row.contacts.some((meta) => meta.meta_key === 'fbpixel' && meta.meta_value === 'y'));
        }

        if (sortField) {
            const metKeyDifference = updatedResults.filter((row) => row.contacts.some((meta) => meta.meta_key === sortField));
            const metKeyWithOurDifference = updatedResults.filter((row) => row.contacts.every((meta) => meta.meta_key !== sortField));
            metKeyDifference.sort((a, b) => {
                const dateA = new Date(a.created_at).getTime();
                const dateB = new Date(b.created_at).getTime();
                return sortOrder === 'asc'
                    ? String(dateA).localeCompare(String(dateB))
                    : String(dateB).localeCompare(String(dateA));
            });
            updatedResults = [...metKeyDifference, ...metKeyWithOurDifference];
        } else {
            updatedResults = updatedResults.sort((a, b) => {
                const valueA: string | number = new Date(a.created_at).getTime();
                const valueB: string | number = new Date(b.created_at).getTime();

                return sortOrder === 'asc'
                    ? String(valueA).localeCompare(String(valueB))
                    : String(valueB).localeCompare(String(valueA));
            });
        }
        if (city.length >= 1) {
            const normalizedCities = city.map((c) => c.trim().toLowerCase());
             updatedResults = updatedResults.filter((r) => normalizedCities.includes(r.location.trim().toLowerCase()));
        }
        if (zip.length > 1) updatedResults = updatedResults.filter((r) => r.contacts.some((c) => c.meta_key === 'zip' && c.meta_value.trim() === zip.trim()));
        // if (searchCategory.length > 1) updatedResults = updatedResults.
        // filter((r) => r.contacts.some((c) => c.meta_key === 'category' && c.meta_value.toLowerCase().trim() === searchCategory.toLowerCase().trim()));
        if (searchCategory.length > 0) {
            updatedResults = updatedResults.filter((r) => searchCategory.includes(r.id));
        }

        Object.entries(selectedFilters).forEach(([key, value]) => {
            if (!value) return;
            const isMarketingKey = [
                'mobilefriendly',
                'fbpixel',
                'gremarketing',
                'ganalytics',
                'lianalytics',
                'uses_wp',
                'uses_shopify'
            ].includes(key.toLowerCase());
            updatedResults = updatedResults.filter((r) => r.contacts.some((c) => {
                    const matchesKey = c.meta_key.toLowerCase().includes(key.toLowerCase());
                    console.log(isMarketingKey, matchesKey);
                    if (isMarketingKey) {
                        return matchesKey && c.meta_value === 'y';
                    }
                    return matchesKey;
                }));
            // if (value) {
            //     updatedResults = updatedResults.filter((r) => r.contacts.some((c) => c.meta_key.toLowerCase().includes(key.toLowerCase())));
            // }
        });
        setFilteredResults(updatedResults);
    }, [sortField, sortOrder, downhillMode, results, city, zip, searchCategory, selectedFilters]);

    const handleDeleteCertificate = async (id: number) => {
        try {
            await dispatch(deleteProspectItemThunk(id)).unwrap();
        } catch (e) {
            //
        }
    };

    const columns: GridColDef<ProspectsRow>[] = [
        { field: 'name', headerName: 'Business', flex: 1 },
        { field: 'location', headerName: 'Location', flex: 1 },
        {
            field: 'contacts',
            headerName: 'Contacts',
            flex: 1,
            renderCell: (params: GridRenderCellParams<ProspectsRow, MetaObject[]>) => <RenderIcons contacts={params.value || []} />
        },
        {
            field: 'actions',
            headerName: 'Actions',
            // flex: 1,
            renderCell: (params: GridRenderCellParams<ProspectsRow, MetaObject[]>) => <Box>
                <IconButton
                    color="error"
                    onClick={() => handleDeleteCertificate(params.row.id)}
                >
                    <DeleteOutlineRounded fontSize="small" color="error" />
                </IconButton>
            </Box>
        }
    ];

    const handleSortSelection = (field: D7MetaKeys | null) => {
        setSortField(field);
    };

    const handlePushToPipeline = async () => {
        try {
            await dispatch(getAllPipelinesThunk(0)).unwrap();
            setIsModalOpen(true);
        } catch (e) {
            // Handle errors
        }
    };

    const closeAndOpenHandler = (value: 'filter' | 'sort' | 'downhill' | 'push', event?: React.MouseEvent<HTMLButtonElement>) => {
        switch (value) {
            case 'filter':
                if (event) {
                    setFilterAnchor(filterAnchor ? null : event?.currentTarget);
                    setIsSortOpen(false);
                    setIsModalOpen(false);
                }
                break;
            case 'sort':
                setIsSortOpen(true);
                setFilterAnchor(null);
                setIsModalOpen(false);
                break;
            case 'downhill':
                setIsSortOpen(false);
                setIsModalOpen(false);
                setFilterAnchor(null);
                setDownhillMode(!downhillMode);
                break;
            case 'push':
                setIsSortOpen(false);
                setFilterAnchor(null);
                handlePushToPipeline();
                break;
            default:
                break;
        }
    };
    const showFilter = city.length >= 1 || zip.length > 1 || searchCategory.length >= 1 || Object.values(selectedFilters).some((value) => value === true);

    return (
        <CardWrapper>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button onClick={setIsProspectOpen} style={{ marginBottom: '10px' }}>
                    &larr; Back
                </Button>
                <Box display="flex" gap="10px">
                    <CustomTooltip title={'This filter shows all deals offered by\nFB Pixel'}>
                        <Button
                            variant="outlined"
                            color={downhillMode ? 'primary' : 'secondary'}
                            onClick={() => closeAndOpenHandler('downhill')}
                        >
                            Downhill Mode
                        </Button>
                    </CustomTooltip>
                    <Button variant={showFilter ? 'contained' : 'outlined'} color={showFilter ? 'primary' : 'secondary'}
                        onClick={(event) => closeAndOpenHandler('filter', event)}
                        startIcon={<FilterAltOutlined />}
                    >
                        Filter
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => closeAndOpenHandler('sort')} sx={{ display: 'none' }} startIcon={<SwapVertOutlined />}>
                        Sort
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => exportToCSV(filteredResults as ProspectsRow[], `${Date.now()}.csv`)}
                        disabled={prospectDetail?.results && prospectDetail?.results.length <= 1}
                        startIcon={<FileDownloadOutlined />}
                    >
                        Export to CSV
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => closeAndOpenHandler('push')}
                        disabled={selectedRows.length === 0}
                        startIcon={<AddOutlined />}
                    >
                        Push to pipeline
                    </Button>
                </Box>
            </Box>

            {/* Filter Popup */}
            <D7FilterPanel
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={() => setFilterAnchor(null)}
                onApply={(values: {
                    // city: string;
                    city: string[];
                    zip: string;
                    searchCategory: number[];
                    // searchCategory: string;
                    filters: Record<string, boolean>;
                }) => {
                    setCity(values.city);
                    setZip(values.zip);
                    setSearchCategory(values.searchCategory);
                    setSelectedFilters(values.filters);
                    setFilterAnchor(null);
                }}
                appliedCity={city}
                appliedZip={zip}
                appliedCategories={searchCategory}
                // appliedCategories={searchCategory.map(String)}
                appliedFilters={selectedFilters}
            />

            <Typography mb={1}>Total {filteredResults.length || 0} {prospectDetail?.niche} businesses -
                <Typography component="span" sx={{ color: brand[300] }}> {prospectDetail?.city}, {prospectDetail?.state}</Typography>
            </Typography>

            {/* Data Grid */}
            <div style={{ height: '68vh', width: '100%' }}>
                <DataGrid
                    rows={filteredResults as ProspectsRow[]}
                    columns={columns}
                    // pagination
                    // paginationModel={paginationModel}
                    // onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 50 }
                        }
                    }}
                    rowSelectionModel={selectedRows}
                    pageSizeOptions={[50, 100]}
                    onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => setSelectedRows(newSelection)}
                    checkboxSelection
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSorting
                    disableAutosize
                    sx={{
                        '& .MuiDataGrid-row--borderBottom': {
                            backgroundColor: `${brand[50]} !important`
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '14px'
                        },
                        'width': '100%',
                        'overflowX': 'hidden'
                    }}
                />
            </div>

            {isModalOpen && <ReviewProspect
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                selectedRows={selectedRows as number[]}
                totalCount={prospectDetail?.results.length || 0}
                setIsProspectOpen={setIsProspectOpen}
            />}

            <SortProspectsModal
                open={isSortOpen}
                onClose={() => setIsSortOpen(false)}
                onSortSelect={handleSortSelection}
                sortField={sortField}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
            />
        </CardWrapper>
    );
};

export default ViewProspects;
